import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase, Users, TrendingUp, Award, Package, Wallet, Calendar,
  FileText, UserPlus, Activity, DollarSign, Plus, Trash2, Edit, X,
  ChevronRight, BarChart3, BookOpen, Clock
} from 'lucide-react';

const API = '/api';

// Sub-navigation tabs
const TABS = [
  { id: 'enterprise', label: 'Mon Entreprise', icon: Briefcase },
  { id: 'clients', label: 'Mes Clients', icon: Users },
  { id: 'inventory', label: 'Mon Inventaire', icon: Package },
  { id: 'resources', label: 'Mes Ressources', icon: Wallet },
];

export const BusinessHubPage = () => {
  const [activeTab, setActiveTab] = useState('enterprise');

  return (
    <div className="min-h-screen bg-[#0a0a0f] p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <h1 data-testid="business-hub-title" className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <Briefcase className="w-7 h-7 text-yellow-400" />
          Centre des Affaires
        </h1>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-yellow-400 text-black'
                  : 'bg-[#12121a] text-gray-400 hover:text-white border border-[#2d2d3a]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'enterprise' && <EnterpriseSection />}
        {activeTab === 'clients' && <ClientsSection />}
        {activeTab === 'inventory' && <InventorySection />}
        {activeTab === 'resources' && <ResourcesSection />}
      </div>
    </div>
  );
};

// ==================== MON ENTREPRISE ====================
const EnterpriseSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/business/performance`);
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Volim Gwoup" value={data?.group_volume || 0} suffix=" PW" color="yellow" />
        <KPICard icon={Award} label="Rang Aktyèl" value={`Nivo ${data?.highest_rank || 0}`} color="red" />
        <KPICard icon={UserPlus} label="Rekrit Dirèk" value={data?.direct_recruits || 0} color="green" />
        <KPICard icon={DollarSign} label="Revni Total" value={`${(data?.total_earnings || 0).toLocaleString()} HTG`} color="blue" />
      </div>

      {/* Recruitment Forms */}
      <RecruitmentSection />

      {/* Activity Log */}
      <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-yellow-400" />
          Rapò d'Aksyon
        </h3>
        {data?.recent_activities?.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {data.recent_activities.map((act, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-[#0a0a0f] rounded-lg">
                <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-white text-sm">{act.description}</p>
                  <p className="text-gray-600 text-xs">{act.created_at ? new Date(act.created_at).toLocaleString() : ''}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Pa gen aktivite ankò</p>
        )}
      </div>
    </div>
  );
};

// ==================== RECRUITMENT ====================
const RecruitmentSection = () => {
  const [showForm, setShowForm] = useState(null); // 'associate' or 'client'
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (type) => {
    setLoading(true);
    try {
      await axios.post(`${API}/business/recruit/${type}`, form);
      toast.success(type === 'associate' ? 'Asosye enskri!' : 'Kliyan ajoute!');
      setShowForm(null);
      setForm({ name: '', email: '', phone: '', password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erè');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <UserPlus className="w-5 h-5 text-green-400" />
        Rekritman
      </h3>

      {!showForm ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={() => setShowForm('associate')} className="p-4 bg-[#0a0a0f] rounded-lg border border-[#2d2d3a] hover:border-green-400/30 text-left transition-all">
            <UserPlus className="w-8 h-8 text-green-400 mb-2" />
            <p className="text-white font-medium">Enskri yon Asosye</p>
            <p className="text-gray-500 text-xs">Nouvo manm nan ekip ou</p>
          </button>
          <button onClick={() => setShowForm('client')} className="p-4 bg-[#0a0a0f] rounded-lg border border-[#2d2d3a] hover:border-blue-400/30 text-left transition-all">
            <Users className="w-8 h-8 text-blue-400 mb-2" />
            <p className="text-white font-medium">Enskri yon Kliyan</p>
            <p className="text-gray-500 text-xs">Kliyan detay pou ou</p>
          </button>
        </div>
      ) : (
        <div className="max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium">{showForm === 'associate' ? 'Nouvo Asosye' : 'Nouvo Kliyan'}</h4>
            <button onClick={() => setShowForm(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-3">
            <input placeholder="Non" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-dark" />
            <input placeholder="Imèl" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-dark" />
            <input placeholder="Telefòn" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="input-dark" />
            {showForm === 'associate' && (
              <input placeholder="Modpas tanporè" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="input-dark" />
            )}
            <button onClick={() => handleSubmit(showForm)} disabled={loading} className="btn-primary w-full py-3">
              {loading ? 'Ap enskri...' : 'Enskri'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MES CLIENTS (CRM) ====================
const ClientsSection = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/crm/clients`);
      setClients(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(); }, []);

  const addClient = async () => {
    try {
      await axios.post(`${API}/crm/clients`, form);
      toast.success('Kliyan ajoute!');
      setShowAdd(false);
      setForm({ name: '', email: '', phone: '', notes: '' });
      fetchClients();
    } catch (e) { toast.error('Erè'); }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Siprime kliyan sa a?')) return;
    await axios.delete(`${API}/crm/clients/${id}`);
    fetchClients();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">CRM - Kliyan Mwen</h3>
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Ajoute
        </button>
      </div>

      {showAdd && (
        <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Non" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-dark" />
            <input placeholder="Imèl" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-dark" />
            <input placeholder="Telefòn" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="input-dark" />
            <input placeholder="Nòt" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input-dark" />
          </div>
          <button onClick={addClient} className="btn-primary mt-3 px-4 py-2">Save</button>
        </div>
      )}

      {clients.length === 0 ? (
        <div className="text-center py-12 bg-[#12121a] rounded-xl border border-[#2d2d3a]">
          <Users className="w-16 h-16 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">Pa gen kliyan ankò</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {clients.map(client => (
            <div key={client.client_id} className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400 font-bold text-sm">{client.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{client.name}</p>
                <p className="text-gray-500 text-xs truncate">{client.email} • {client.phone}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${client.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {client.status}
              </span>
              <button onClick={() => deleteClient(client.client_id)} className="text-gray-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MON INVENTAIRE ====================
const InventorySection = () => {
  const [items, setItems] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', sku: '', quantity: 0, unit_price: 0, category: '' });
  const [showLedger, setShowLedger] = useState(false);

  const fetchData = async () => {
    try {
      const [itemsRes, ledgerRes] = await Promise.all([
        axios.get(`${API}/inventory/stock`),
        axios.get(`${API}/inventory/ledger`)
      ]);
      setItems(itemsRes.data);
      setLedger(ledgerRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const addItem = async () => {
    try {
      await axios.post(`${API}/inventory/stock`, form);
      toast.success('Atik ajoute!');
      setShowAdd(false);
      setForm({ name: '', sku: '', quantity: 0, unit_price: 0, category: '' });
      fetchData();
    } catch (e) { toast.error('Erè'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <button onClick={() => setShowLedger(false)} className={`text-sm font-medium px-3 py-1 rounded ${!showLedger ? 'bg-yellow-400 text-black' : 'text-gray-400'}`}>Stock</button>
          <button onClick={() => setShowLedger(true)} className={`text-sm font-medium px-3 py-1 rounded ${showLedger ? 'bg-yellow-400 text-black' : 'text-gray-400'}`}>Ledger</button>
        </div>
        {!showLedger && (
          <button onClick={() => setShowAdd(!showAdd)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Ajoute
          </button>
        )}
      </div>

      {showAdd && !showLedger && (
        <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <input placeholder="Non Atik" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-dark" />
            <input placeholder="SKU" value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} className="input-dark" />
            <input placeholder="Kantite" type="number" value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 0})} className="input-dark" />
            <input placeholder="Pri Inite (HTG)" type="number" value={form.unit_price} onChange={e => setForm({...form, unit_price: parseInt(e.target.value) || 0})} className="input-dark" />
            <input placeholder="Kategori" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-dark" />
          </div>
          <button onClick={addItem} className="btn-primary mt-3 px-4 py-2">Save</button>
        </div>
      )}

      {!showLedger ? (
        items.length === 0 ? (
          <div className="text-center py-12 bg-[#12121a] rounded-xl border border-[#2d2d3a]">
            <Package className="w-16 h-16 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">Pa gen atik nan envantè a</p>
          </div>
        ) : (
          <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-[#2d2d3a]">
                <th className="text-left text-xs text-gray-500 p-3">Atik</th>
                <th className="text-left text-xs text-gray-500 p-3">SKU</th>
                <th className="text-right text-xs text-gray-500 p-3">Kantite</th>
                <th className="text-right text-xs text-gray-500 p-3">Pri</th>
              </tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.item_id} className="border-b border-[#2d2d3a]/50 hover:bg-white/5">
                    <td className="p-3 text-white text-sm">{item.name}</td>
                    <td className="p-3 text-gray-500 text-xs">{item.sku}</td>
                    <td className="p-3 text-right text-white font-medium">{item.quantity}</td>
                    <td className="p-3 text-right text-yellow-400 text-sm">{item.unit_price?.toLocaleString()} HTG</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        ledger.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Pa gen antre nan ledger la</p>
        ) : (
          <div className="space-y-2">
            {ledger.map(entry => (
              <div key={entry.ledger_id} className="bg-[#12121a] rounded-lg border border-[#2d2d3a] p-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${entry.type === 'in' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <span className={`text-xs font-bold ${entry.type === 'in' ? 'text-green-400' : 'text-red-400'}`}>{entry.type === 'in' ? '+' : '-'}</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm">{entry.item_name} — {entry.reason || entry.type}</p>
                  <p className="text-gray-600 text-xs">{entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}</p>
                </div>
                <span className={`font-bold text-sm ${entry.type === 'in' ? 'text-green-400' : 'text-red-400'}`}>{entry.type === 'in' ? '+' : '-'}{entry.quantity}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ==================== MES RESSOURCES ====================
const ResourcesSection = () => {
  const [wallet, setWallet] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('wallet');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [walletRes, matRes, eventRes] = await Promise.all([
          axios.get(`${API}/resources/wallet`),
          axios.get(`${API}/resources/marketing-kit`),
          axios.get(`${API}/resources/agenda`)
        ]);
        setWallet(walletRes.data);
        setMaterials(matRes.data);
        setEvents(eventRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {[{id:'wallet', label:'Wallet', icon: Wallet}, {id:'marketing', label:'Marketing Kit', icon: FileText}, {id:'agenda', label:'Agenda', icon: Calendar}].map(t => (
          <button key={t.id} onClick={() => setActiveSubTab(t.id)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${activeSubTab === t.id ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-[#12121a] text-gray-400 border border-[#2d2d3a]'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'wallet' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5 text-center">
              <p className="text-gray-500 text-xs mb-1">Balans</p>
              <p className="text-3xl font-bold text-yellow-400">{(wallet?.balance || 0).toLocaleString()} HTG</p>
            </div>
            <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5 text-center">
              <p className="text-gray-500 text-xs mb-1">Total Touche</p>
              <p className="text-2xl font-bold text-green-400">{(wallet?.total_earned || 0).toLocaleString()} HTG</p>
            </div>
            <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-5 text-center">
              <p className="text-gray-500 text-xs mb-1">Total Retire</p>
              <p className="text-2xl font-bold text-red-400">{(wallet?.total_withdrawn || 0).toLocaleString()} HTG</p>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'marketing' && (
        materials.length === 0 ? (
          <div className="text-center py-12 bg-[#12121a] rounded-xl border border-[#2d2d3a]">
            <FileText className="w-16 h-16 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">Materyèl marketing ap disponib byento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {materials.map(m => (
              <a key={m.material_id} href={m.url} target="_blank" rel="noopener noreferrer" className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-4 hover:border-yellow-400/30 flex items-center gap-4">
                <FileText className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">{m.title}</p>
                  <p className="text-gray-500 text-xs">{m.description}</p>
                </div>
              </a>
            ))}
          </div>
        )
      )}

      {activeSubTab === 'agenda' && (
        events.length === 0 ? (
          <div className="text-center py-12 bg-[#12121a] rounded-xl border border-[#2d2d3a]">
            <Calendar className="w-16 h-16 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">Pa gen evènman pou kounye a</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <div key={event.event_id} className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-4 flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-yellow-400/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{event.title}</p>
                  <p className="text-gray-500 text-sm">{event.description}</p>
                  <p className="text-yellow-400 text-xs mt-1">{event.date ? new Date(event.date).toLocaleDateString() : ''} • {event.location}</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ==================== SHARED COMPONENTS ====================
const LoadingSpinner = () => (
  <div className="flex justify-center py-12">
    <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const KPICard = ({ icon: Icon, label, value, suffix = '', color = 'yellow' }) => {
  const colors = { yellow: 'text-yellow-400', red: 'text-red-400', green: 'text-green-400', blue: 'text-blue-400' };
  return (
    <div className="bg-[#12121a] rounded-xl border border-[#2d2d3a] p-4">
      <Icon className={`w-5 h-5 ${colors[color]} mb-2`} />
      <p className={`text-xl font-bold ${colors[color]}`}>{value}{suffix}</p>
      <p className="text-gray-500 text-xs">{label}</p>
    </div>
  );
};
